<script setup lang="ts">
import StyledDateInput from "@/components/StyledDateInput.vue";
import LoadingSpin from "@/components/icons/LoadingSpin.vue";
import useAuthUser from "@/composables/useAuthUser";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import { Media } from "@/interfaces/media";
import { ref } from "vue";
import VueEasyLightbox from "vue-easy-lightbox";
import HasPermission from "./HasPermission.vue";
import StyledButton from "./StyledButton.vue";

interface Props {
  media: Media;
  title?: string;
  fileName?: string;
  alt?: string;
  description?: string;
  expiresAt?: string;
  namePermissionUpdate?: string;
  namePermissionDelete?: string;
  entityType?: string;
}

interface Emits {
  (event: "toggle:editingMode"): void;
  (event: "update:modelValue", value: any): void;
  (event: "update:media"): void;
  (event: "delete:media"): void;
}

defineProps<Props>();
defineEmits<Emits>();
const visible = ref(false);
const index = ref();
const isLoaded = ref(false);

const handleHide = () => {
  visible.value = false;
};

const { hasPermissionUser } = useHasPermissionUser();
</script>

<template>
  <div class="flex flex-1 flex-col gap-2 w-full">
    <h3 v-if="title" class="font-medium">{{ title }}</h3>

    <section class="flex h-full flex-col gap-4 justify-between">
      <div
        class="flex flex-col justify-between gap-4 p-2 w-full border rounded-md"
      >
        <div class="flex flex-col justify-between">
          <p class="text-sm font-bold">
            Archivo:
            <span class="font-normal text-sm">{{ media.fileName }}</span>
          </p>
        </div>

        <img
          v-show="isLoaded"
          v-tooltip="'Ver imagen'"
          class="flex-1 max-w-[12.3rem] aspect-square object-contain cursor-pointer mx-auto"
          :src="media.originalUrl"
          :alt="alt"
          @click="
            visible = true;
            index = 0;
          "
          @load="isLoaded = true"
        />

        <div v-if="!isLoaded" class="mx-auto py-10">
          <LoadingSpin spin-class="w-16 h-16" :dark="true" />
        </div>
      </div>

      <p v-if="!expiresAt" class="text-red-500 text-sm">
        Este cambio no será guardado. Por favor, seleccione una fecha de
        vigencia.
      </p>

      <div class="flex justify-between gap-4">
        <StyledDateInput
          :min-date="new Date()"
          :model-value="expiresAt"
          @update:model-value="($event) => $emit('update:modelValue', $event)"
          placeholder="Fecha de vigencia"
          class="w-full"
          :disabled="
            !hasPermissionUser(namePermissionUpdate || '', entityType || '')
          "
        />

        <div class="flex gap-2">
          <HasPermission
            :name-permission="namePermissionDelete || ''"
            :entity-type="entityType || ''"
          >
            <StyledButton
              v-tooltip="'Remover'"
              class="border"
              @click="$emit('delete:media')"
            >
              <span class="material-symbols-outlined text-[20px]">
                delete
              </span>
            </StyledButton>
          </HasPermission>

          <HasPermission
            :name-permission="namePermissionUpdate || ''"
            :entity-type="entityType || ''"
          >
            <StyledButton
              v-tooltip="'Editar'"
              class="border"
              @click="$emit('toggle:editingMode')"
            >
              <span class="material-symbols-outlined text-[20px]"> edit </span>
            </StyledButton>
          </HasPermission>
        </div>
      </div>
    </section>
  </div>

  <div>
    <VueEasyLightbox
      esc-enable
      :move-disabled="true"
      :visible="visible"
      :imgs="[media.originalUrl]"
      :index="index"
      @hide="handleHide"
    ></VueEasyLightbox>
  </div>
</template>

<style scoped></style>
